import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import ScrollAnimation from '../components/ScrollAnimation';
import { companyInfo } from '../data/mock';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { saveContactForm } from '../firebase/contactService';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Save to Firebase Firestore
      const docId = await saveContactForm(formData);
      
      // Show success message
      setIsSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitError(`Failed to send message: ${error.message}. Please try again or contact us directly.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <ScrollAnimation>
            <h1 className="text-5xl md:text-7xl font-league-spartan font-extralight text-gray-900 mb-6 tracking-tight leading-none">
              Contact Us
            </h1>
          </ScrollAnimation>
          <ScrollAnimation delay={200}>
            <p className="text-xl text-gray-600 font-league-spartan font-light mb-12 max-w-3xl mx-auto leading-relaxed">
              Ready to transform your business with AI? Let's discuss your project and explore how we can help you achieve your goals.
            </p>
          </ScrollAnimation>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <ScrollAnimation>
              <Card className="bg-white border border-gray-200 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-league-spartan font-light text-gray-900 mb-8">
                    Send us a message
                  </h2>
                  
                  {isSubmitted && (
                    <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-primary font-league-spartan">
                        Thank you! Your message has been sent successfully. We'll get back to you soon.
                      </span>
                    </div>
                  )}

                  {submitError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <span className="text-red-600 font-league-spartan">
                        {submitError}
                      </span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-league-spartan font-medium text-gray-700 mb-2">
                          Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="bg-gray-50 border-gray-200 focus:border-primary focus:ring-primary"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-league-spartan font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="bg-gray-50 border-gray-200 focus:border-primary focus:ring-primary"
                          placeholder="your.email@company.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-league-spartan font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="bg-gray-50 border-gray-200 focus:border-primary focus:ring-primary"
                        placeholder="What can we help you with?"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-league-spartan font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="bg-gray-50 border-gray-200 focus:border-primary focus:ring-primary resize-none"
                        placeholder="Tell us about your project, goals, and any specific requirements..."
                      />
                    </div>
                    
                    <Button 
                      type="submit"
                      enabled={isSubmitting}
                      className="w-full"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </ScrollAnimation>

            {/* Contact Information */}
            <ScrollAnimation delay={200}>
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-league-spartan font-light text-gray-900 mb-8">
                    Get in touch
                  </h2>
                  <p className="text-gray-600 font-league-spartan leading-relaxed mb-8">
                    We'd love to hear from you. Whether you have a specific project in mind or just want to explore possibilities, our team is here to help you navigate the world of AI solutions.
                  </p>
                </div>

                {/* Contact Details */}
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-league-spartan font-medium text-gray-900 mb-2">Email</h3>
                      <a 
                        href={`mailto:${companyInfo.contact.email}`}
                        className="text-gray-600 font-league-spartan hover:text-primary transition-colors"
                      >
                        {companyInfo.contact.email}
                      </a>
                    </div>
                  </div>


                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-league-spartan font-medium text-gray-900 mb-2">Address</h3>
                      <p className="text-gray-600 font-league-spartan">
                        {companyInfo.contact.address}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Response Time */}
                <Card className="bg-primary/5 border border-primary/20">
                  <CardContent className="p-6">
                    <h3 className="font-league-spartan font-medium text-gray-900 mb-3">Response Time</h3>
                    <p className="text-gray-700 font-league-spartan text-sm leading-relaxed">
                      We typically respond to all inquiries within 24 hours during business days.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;